/**
 * Intellesphere Landing Page
 *
 * Visual language: Google-inspired palette on a white canvas.
 *   Blue   #4285F4
 *   Red    #EA4335
 *   Yellow #FBBC04
 *   Green  #34A853
 *
 * The palette lives in `src/styles/intellesphere-landing.css` as `g-*` utility
 * classes; this page only references those class names.
 */
import { useNavigate } from 'react-router';
import { Button } from '../components/ui/button';
import { ArrowRight, Users, Shield, Target, Globe, Heart, TrendingUp, LogIn } from 'lucide-react';

type Accent = 'blue' | 'red' | 'yellow' | 'green';

const ACCENT_TEXT: Record<Accent, string> = {
  blue: 'g-text-blue',
  red: 'g-text-red',
  yellow: 'g-text-yellow',
  green: 'g-text-green',
};

const ACCENT_BG_SOFT: Record<Accent, string> = {
  blue: 'g-bg-blue-soft',
  red: 'g-bg-red-soft',
  yellow: 'g-bg-yellow-soft',
  green: 'g-bg-green-soft',
};

const ACCENT_BORDER_L: Record<Accent, string> = {
  blue: 'g-border-l-blue',
  red: 'g-border-l-red',
  yellow: 'g-border-l-yellow',
  green: 'g-border-l-green',
};

interface Pillar {
  icon: typeof Users;
  title: string;
  body: string;
  color: Accent;
}

const PILLARS: Pillar[] = [
  {
    icon: Users,
    title: 'Human-Centered Innovation',
    body: 'We prioritize the needs and well-being of people over corporate interests, designing AI solutions with user preferences in mind.',
    color: 'blue',
  },
  {
    icon: Shield,
    title: 'Responsible and Accessible AI',
    body: 'Transparency and responsible development are paramount. We build unbiased, privacy-preserving solutions beneficial to society.',
    color: 'red',
  },
  {
    icon: Target,
    title: 'Meticulous Execution',
    body: 'Relentless pursuit for excellence. We deliver innovative AI solutions that exceed expectations and set new benchmarks for quality.',
    color: 'yellow',
  },
  {
    icon: Globe,
    title: 'Partnership for Progress',
    body: 'We collaborate with trusted organizations in finance, education, and government to leverage AI for positive change.',
    color: 'green',
  },
  {
    icon: Heart,
    title: 'Employee Ownership & Social Impact',
    body: 'An employee-owned company prioritizing social impact, aligning profits with the well-being of the people we serve.',
    color: 'blue',
  },
  {
    icon: TrendingUp,
    title: 'South Asian Leadership',
    body: 'Leading employer of mathematicians, statisticians, and visionaries from South Asia, fueling responsible AI development.',
    color: 'red',
  },
];

interface CoreValue {
  title: string;
  body: string;
  color: Accent;
}

const CORE_VALUES: CoreValue[] = [
  {
    title: 'Human-Centered Innovation',
    body: 'We prioritize the needs and well-being of people over corporate interests. Our "People-Centered Innovation" approach ensures AI solutions are designed with user preferences and real-world challenges in mind.',
    color: 'blue',
  },
  {
    title: 'Continuous Learning',
    body: 'We embrace a growth mindset, constantly seeking to improve our AI capabilities and adapt to the evolving technological landscape. Collaboration, knowledge sharing, and integrity are key to our success.',
    color: 'green',
  },
  {
    title: 'Ethical Development',
    body: 'Transparency and responsible AI development are paramount. We strive to build trust by ensuring our solutions are unbiased, privacy-preserving, and beneficial to society as a whole.',
    color: 'red',
  },
  {
    title: 'Meticulous Execution with Highest Standards',
    body: 'We are committed to flawless execution, delivering innovative AI solutions that exceed expectations and set new benchmarks for quality. We work for the trust of customers everyday by ensuring no compromise on the highest standards.',
    color: 'yellow',
  },
  {
    title: 'Partnership for Progress',
    body: 'We believe in collaboration to achieve our goals. We partner with trusted organizations in finance, education, and the government to leverage AI for financial inclusion, national security, and social well-being.',
    color: 'blue',
  },
  {
    title: 'Employee Ownership and Social Impact',
    body: 'Intellisphere AI is an employee-owned company, aligning our profits with the well-being of the people we serve. We prioritize social impact over shareholder interests, using AI for positive change across South Asian countries. We are the leading employer of mathematicians, statisticians, and visionaries from South Asian countries.',
    color: 'green',
  },
];

export default function IntelleSphereLanding() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-white text-slate-900">
      {/* Top Navigation Bar */}
      <nav className="fixed top-0 left-0 right-0 bg-white/95 backdrop-blur-md border-b border-slate-200 z-50">
        <div className="container mx-auto px-4 py-3 flex items-center justify-between">
          {/* Wordmark cycles blue → pink → violet → red */}
          <div className="flex items-center font-semibold text-xl tracking-tight">
            <span className="g-text-blue">I</span>
            <span className="g-text-yellow">n</span>
            <span className="g-text-green">t</span>
            <span className="g-text-red">e</span>
            <span className="g-text-blue">l</span>
            <span className="g-text-yellow">l</span>
            <span className="text-slate-900">esphere</span>
          </div>
          <Button
            onClick={() => navigate('/login')}
            variant="outline"
            className="gap-2 border-slate-300 text-slate-700 hover:bg-slate-100"
          >
            <LogIn className="h-4 w-4" />
            Sign In
          </Button>
        </div>
      </nav>

      {/* Hero Section */}
      <header className="container mx-auto px-4 py-12 sm:py-16 lg:py-24 mt-16">
        <div className="text-center max-w-4xl mx-auto">
          <h1 className="text-4xl sm:text-5xl md:text-6xl font-semibold tracking-tight mb-4 sm:mb-6">
            <span className="g-text-blue">I</span>
            <span className="g-text-yellow">n</span>
            <span className="g-text-green">t</span>
            <span className="g-text-red">e</span>
            <span className="g-text-blue">l</span>
            <span className="g-text-yellow">l</span>
            <span className="text-slate-900">esphere Ltd.</span>
          </h1>
          <p className="text-xl sm:text-2xl font-medium mb-6 sm:mb-8 g-text-blue">
            Empowering People through Responsible AI
          </p>
          <p className="text-base sm:text-lg text-slate-600 mb-8 sm:mb-12 leading-relaxed px-4">
            We&rsquo;re on a mission to bridge the technological divide and empower individuals across the globe.
            We prioritize social impact over profit, fostering a future where AI isn&rsquo;t just powerful,
            but accessible and works for billions &mdash; not just the privileged few.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center px-4">
            <Button
              size="lg"
              onClick={() => navigate('/project')}
              className="g-bg-blue text-base sm:text-lg px-6 sm:px-8 py-5 sm:py-6 w-full sm:w-auto text-white hover:brightness-110 shadow-sm rounded-full"
            >
              View Project Management <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
            <Button
              size="lg"
              variant="outline"
              onClick={() => {
                document.getElementById('mission')?.scrollIntoView({ behavior: 'smooth' });
              }}
              className="text-base sm:text-lg px-6 sm:px-8 py-5 sm:py-6 w-full sm:w-auto rounded-full border-slate-300 text-slate-700 hover:bg-slate-50"
            >
              Learn More
            </Button>
          </div>

          {/* Google-style accent bar */}
          <div className="mt-12 sm:mt-16 flex justify-center gap-1.5">
            <span className="g-bg-blue h-1.5 w-12 rounded-full" />
            <span className="g-bg-red h-1.5 w-12 rounded-full" />
            <span className="g-bg-yellow h-1.5 w-12 rounded-full" />
            <span className="g-bg-green h-1.5 w-12 rounded-full" />
          </div>
        </div>
      </header>

      {/* Mission Statement */}
      <section id="mission" className="bg-slate-50 border-y border-slate-200 py-12 sm:py-16 lg:py-20">
        <div className="container mx-auto px-4 max-w-4xl">
          <h2 className="text-2xl sm:text-3xl font-semibold mb-3 text-center text-slate-900">
            Our Mission
          </h2>
          <div className="g-bg-blue mx-auto mb-6 h-1 w-16 rounded-full" />
          <p className="text-base sm:text-lg leading-relaxed text-center text-slate-700">
            With relentless pursuit of excellence from the brightest minds in mathematics, statistics,
            and visionaries, we strive for the highest quality in user-centric AI solutions to earn
            the trust everyday of our partners! Through partnerships with trusted organizations in
            finance, education, and the government, we leverage AI to tackle real-world challenges,
            promote financial inclusion, enhance national security, and improve social well-being.
          </p>
        </div>
      </section>

      {/* Core Pillars */}
      <section className="py-12 sm:py-16 lg:py-20 bg-white">
        <div className="container mx-auto px-4">
          <h2 className="text-2xl sm:text-3xl font-semibold mb-3 text-center text-slate-900">
            What We Stand For
          </h2>
          <div className="g-bg-red mx-auto mb-10 h-1 w-16 rounded-full" />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8 max-w-6xl mx-auto">
            {PILLARS.map((pillar) => {
              const Icon = pillar.icon;
              return (
                <div
                  key={pillar.title}
                  className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm transition-all hover:shadow-md hover:-translate-y-0.5"
                >
                  <div className={`mb-4 flex h-12 w-12 items-center justify-center rounded-full ${ACCENT_BG_SOFT[pillar.color]}`}>
                    <Icon className={`h-6 w-6 ${ACCENT_TEXT[pillar.color]}`} />
                  </div>
                  <h3 className="text-lg sm:text-xl font-semibold mb-2 text-slate-900">{pillar.title}</h3>
                  <p className="text-sm sm:text-base text-slate-600 leading-relaxed">{pillar.body}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Core Values */}
      <section className="bg-slate-50 border-y border-slate-200 py-12 sm:py-16 lg:py-20">
        <div className="container mx-auto px-4 max-w-5xl">
          <h2 className="text-2xl sm:text-3xl font-semibold mb-3 text-center text-slate-900">
            Core Values
          </h2>
          <div className="g-bg-green mx-auto mb-10 h-1 w-16 rounded-full" />
          <div className="space-y-4 sm:space-y-5">
            {CORE_VALUES.map((value) => (
              <div
                key={value.title}
                className={`rounded-xl bg-white p-5 sm:p-6 shadow-sm border border-slate-200 hover:shadow-md transition-shadow ${ACCENT_BORDER_L[value.color]}`}
              >
                <h3 className="text-lg sm:text-xl font-semibold mb-2 text-slate-900">
                  {value.title}
                </h3>
                <p className="text-sm sm:text-base text-slate-600 leading-relaxed">{value.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-12 sm:py-16 lg:py-20 bg-white">
        <div className="container mx-auto px-4 text-center max-w-3xl">
          <h2 className="text-2xl sm:text-3xl font-semibold mb-4 sm:mb-6 text-slate-900">
            Join Us in Shaping the Future
          </h2>
          <div className="mx-auto mb-6 flex justify-center gap-1">
            <span className="g-bg-blue h-1 w-8 rounded-full" />
            <span className="g-bg-red h-1 w-8 rounded-full" />
            <span className="g-bg-yellow h-1 w-8 rounded-full" />
            <span className="g-bg-green h-1 w-8 rounded-full" />
          </div>
          <p className="text-base sm:text-lg text-slate-600 mb-6 sm:mb-8 max-w-2xl mx-auto px-4">
            Be part of a movement that&rsquo;s making AI accessible, responsible, and beneficial for billions.
          </p>
          <Button
            size="lg"
            onClick={() => navigate('/project')}
            className="g-bg-blue text-base sm:text-lg px-6 sm:px-8 py-5 sm:py-6 text-white hover:brightness-110 shadow-sm rounded-full"
          >
            Explore ProjectFlow <ArrowRight className="ml-2 h-5 w-5" />
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-white border-t border-slate-200 py-8 sm:py-12">
        <div className="container mx-auto px-4 text-center">
          <p className="text-sm sm:text-base text-slate-700">
            © 2026 Intellesphere.com Ltd. All rights reserved.
          </p>
          <p className="text-sm text-slate-500 mt-2">
            Empowering People through Responsible AI
          </p>
        </div>
      </footer>
    </div>
  );
}
